<template>
  <section class="universes-view">
    <header class="universes-view__header">
      <div class="universes-view__first">
        <h1 class="universes-view__title">
          {{ $t('UniversesView.label.header.title') }}
        </h1>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="universes-view__button ma-2"
              outlined
              depressed
              fab
              color="var(--ds-color-text-1)"
              v-bind="attrs"
              v-on="on"
              @click="openDashboardModal"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('UniversesView.label.header.button') }}</span>
        </v-tooltip>
      </div>
      <p class="universes-view__subtitle">
        {{ $t('UniversesView.label.header.subtitle') }}
      </p>
    </header>

    <div class="universes-view__search">
      <component
        :is="filter.component"
        v-for="(filter, index) in filtersObj"
        :key="index"
        v-model="tempFilter[filter.filterType]"
        v-bind="filter.props"
      />
      <div class="universes-view__search-buttons">
        <button
          class="universes-view__clear-button"
          light
          @click="clearPage()"
        >
          {{ $t('UniversesView.label.button.clearButton') }}
        </button>
        <DsButton
          color="secondary"
          @click="clearFilters()"
        >
          {{ $t('UniversesView.label.button.cancelButton') }}
        </DsButton>
        <DsButton
          :disabled="hasFilter"
          @click="loadDashboards()"
        >
          {{ $t('UniversesView.label.button.applyButton') }}
        </DsButton>
      </div>
    </div>

    <div class="universes-view__separator" />

    <div v-if="initialState">
      <div />
    </div>
    <div
      v-else
      class="universes-view__visualization"
    >
      <div
        v-if="error"
        class="universes-view__visualization"
      >
        <div class="universes-view__info">
          <span class="universes-view__info-title">
            {{ $t('UniversesView.label.message.dataLoadingError.title') }}
          </span>
          <p class="universes-view__info-description">
            {{ $t('UniversesView.label.message.dataLoadingError.description') }}
          </p>
          <DsButton
            color="secondary"
            @click="loadDashboards()"
          >
            {{ $t('UniversesView.label.button.tryAgainButton') }}
          </DsButton>
        </div>
      </div>
      <div
        v-else
        class="universes-view__visualization"
      >
        <v-data-table
          v-if="success"
          class="universes-view__data-table"
          light
          :headers="headers"
          :items="dashboards"
          :items-per-page="10"
          :search="search"
          :loading-text="$t('UniversesView.notification.loadingData')"
          :loading="loading"
          :footer-props="{ itemsPerPageOptions: itemsPerPage }"
        >
          <template v-slot:[`item.expiresTrialDate`]="{ item }">
            <span>{{ getFormattedDate(item) }}</span>
          </template>
          <template v-slot:[`item.isStaging`]="{ item }">
            <span>{{ getLabel(item) }}</span>
          </template>
          <template v-slot:[`item.userEmails`]="{ item }">
            <span>{{ getUsers(item) }}</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <a
              :href="`https://studio.stilingue.com.br/${item.accountId}/${item.universeId}/dashboard`"
              target="_blank"
            ><DsButton
              icon
              name="link"
              color="ghost"
            /></a>

            <DsButton
              icon
              name="edit"
              color="ghost"
              @click="setDashboardToEdit(item)"
            />

            <DsButton
              v-if="showDeleteDashboardFeature"
              icon
              name="trash"
              color="ghost"
              @click="openDeleteModal(item)"
            />
          </template>
        </v-data-table>
        <div
          v-else
          class="universes-view__info"
        >
          <span class="universes-view__info-title">
            {{ $t('UniversesView.label.message.noDataFinding.title') }}
          </span>
          <p class="universes-view__info-description">
            {{ $t('UniversesView.label.message.noDataFinding.description') }}
          </p>
        </div>
      </div>
    </div>

    <UniversesModal
      v-if="universesModal"
      v-model="universesModal"
      :dashboard="dashboardToEdit"
      :modal-title="modalTitle"
      @load-dashboards="loadDashboards"
    />

    <DeleteUniverseModal
      v-model="deleteModal"
      :dashboard="dashboardSelected"
      @close-delete-modal="closeDeleteModal"
      @close-delete-modal-and-reload="closeDeleteModalAndReload"
    />
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import requestStatusMixin from '@/mixins/request-status.mixin';
import UniversesModal from '@/components/UniversesModal';
import { MUTATE } from '@/store/modules/mutation_types';
import { DsButton } from '@ds/components';
import UniversesFilter from '@/components/filters/UniversesFilter.vue';
import DeleteUniverseModal from '@/components/DeleteUniverseModal.vue';
import TextFilter from '@/components/filters/TextFilter.vue';
import SelectFilter from '@/components/filters/SelectFilter.vue';
import DateFilter from '@/components/filters/DateFilter.vue';
import { Flags } from '@/plugins/rollout';

export default {
  name: 'UniversesView',
  components: {
    DsButton,
    UniversesModal,
    UniversesFilter,
    DeleteUniverseModal,
    TextFilter,
    SelectFilter,
    DateFilter,
  },
  mixins: [
    requestStatusMixin,
  ],
  data() {
    return {
      search: '',
      tempFilter: {},
      dashboardToEdit: null,
      requestStatus: 'loading',
      headers: [
        {
          text: this.$t('UniversesView.label.table.accountId'),
          value: 'accountId',
        },
        {
          text: this.$t('UniversesView.label.table.universeId'),
          value: 'universeId',
        },
        {
          text: this.$t('UniversesView.label.table.dashboardId'),
          value: 'dashboardId',
        },
        {
          text: this.$t('UniversesView.label.table.title'),
          value: 'title',
        },
        {
          text: this.$t('UniversesView.label.table.trialDate'),
          value: 'expiresTrialDate',
        },
        {
          text: this.$t('UniversesView.label.table.environment'),
          value: 'isStaging',
        },
        {
          text: this.$t('UniversesView.label.table.users'),
          value: 'userEmails',
        },
        {
          text: '',
          value: 'actions',
          sortable: false
        },
      ],
      itemsPerPage: [5, 10, 15],
      dashboardSelected: null,
      deleteModal: false,
    };
  },
  computed: {
    ...mapState('dashboard', [
      'universes',
      'success',
      'error',
      'initialState'
    ]),
    ...mapState('misc', [
      'formModal',
      'universes',
    ]),
    ...mapGetters('dashboard', [
      'dashboards'
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled'
    ]),
    universesModal: {
      get() {
        return this.formModal;
      },
      set(newValue) {
        this.mutateMisc({
          property: 'formModal',
          with: newValue,
        });
      },
    },
    modalTitle() {
      return this.dashboardToEdit !== null ? this.$t('UniversesModal.label.title.edit') : this.$t('UniversesModal.label.title.create');
    },
    hasFilter() {
      for (const [key, value] of Object.entries(this.tempFilter)) {
        if (value.length === 0) {
          delete this.tempFilter[key];
        }
      }
      return Boolean(!Object.entries(this.tempFilter).length);
    },
    filtersObj() {
      return [
        {
          component: 'UniversesFilter',
          filterType: 'accountId',
          props: {
            label: this.$t('UniversesView.label.filter.accountId'),
            hint: this.$t('UniversesModal.label.message.error.invalidInfo'),
            characters: 16,
            isNumberRule: true,
          }
        },
        {
          component: 'UniversesFilter',
          filterType: 'universeId',
          props: {
            label: this.$t('UniversesView.label.filter.universeId'),
            hint: this.$t('UniversesModal.label.message.error.invalidInfo'),
            characters: 16,
            isNumberRule: true,
          }
        },
        {
          component: 'UniversesFilter',
          filterType: 'dashboardId',
          props: {
            label: this.$t('UniversesView.label.filter.dashboardId'),
            hint: this.$t('UniversesModal.label.message.error.dashboard'),
            characters: 99,
          }
        },
        {
          component: 'TextFilter',
          filterType: 'title',
          props: {
            label: this.$t('UniversesView.label.filter.title'),
          }
        },
        {
          component: 'TextFilter',
          filterType: 'userEmails',
          props: {
            label: this.$t('UniversesView.label.filter.users'),
            emailRule: true,
          }
        },
        {
          component: 'SelectFilter',
          filterType: 'contractModel',
          props: {
            label: this.$t('UniversesView.label.filter.contract'),
            selectItems: ['Todos', 'Teste', 'Contratado'],
          }
        },
        {
          component: 'SelectFilter',
          filterType: 'isStaging',
          props: {
            label: this.$t('UniversesView.label.filter.env'),
            selectItems: ['Todos', 'Homologação', 'Produção'],
          }
        },
        {
          component: 'SelectFilter',
          filterType: 'isPublished',
          props: {
            label: this.$t('UniversesView.label.filter.published'),
            selectItems: ['Todos', 'Ativo', 'Inativo'],
          }
        },
        {
          component: 'DateFilter',
          filterType: 'createdDate',
          props: {
            label: this.$t('UniversesView.label.filter.createDate'),
          }
        },
        {
          component: 'DateFilter',
          filterType: 'updatedDate',
          props: {
            label: this.$t('UniversesView.label.filter.lastUpdateDate'),
          }
        },
      ];
    },
    showDeleteDashboardFeature() {
      return this.isEnabled(Flags.showDeleteDashboard);
    },
  },
  async created() {
    this.clearPage();
    this.requestStatus = 'success';
  },
  methods: {
    ...mapActions('dashboard', [
      'getUniverses',
      'getDashboards',
      'clearUniverses',
    ]),
    ...mapMutations('misc', {
      mutateMisc: MUTATE
    }),
    ...mapMutations('dashboard', {
      mutateDashboard: MUTATE
    }),
    async loadDashboards() {
      if (!Boolean(Object.keys(this.tempFilter).length)) return false;

      await this.getDashboards(this.tempFilter);
    },
    openDashboardModal() {
      this.mutateMisc({
        property: 'formModal',
        with: true,
      });

      this.dashboardToEdit = null;
    },
    setDashboardToEdit(dashboard) {
      this.mutateMisc({
        property: 'formModal',
        with: true,
      });
      this.dashboardToEdit = dashboard;
    },
    openDeleteModal(dashboard) {
      this.dashboardSelected = dashboard;
      this.deleteModal = true;
    },
    closeDeleteModal(value) {
      this.deleteModal = value;
    },
    closeDeleteModalAndReload(value) {
      this.dashboardSelected = null;
      this.loadDashboards();
      this.closeDeleteModal(value);
    },
    getFormattedDate(item) {
      if (!item.expiresTrialDate) return '---';

      const [year, month, day] = item.expiresTrialDate?.toDate().toISOString().substr(0, 10).split('-');
      return `${day}/${month}/${year}`;
    },
    getLabel(item) {
      return item.isStaging ? this.$t('FormModal.label.table.homologEnv') : this.$t('FormModal.label.table.prodEnv');
    },
    getUsers(item) {
      return item.userEmails?.length;
    },
    clearFilters() {
      this.tempFilter = {};
    },
    clearPage() {
      this.tempFilter = {};
      this.clearUniverses();
      this.mutateDashboard({
        property: 'error',
        with: false,
      });
    }
  },
};
</script>

<style scoped lang="scss">
  .universes-view {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--ds-spacing-inline-sm) 85px var(--ds-spacing-inline-sm) 38px;
    width: 100%;
    color: var(--default-text-color);

    &__header {
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      align-items: start;
      width: 1198px;
    }

    &__first {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }

    &__title {
      color: var(--ds-color-text-1);
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-xl);
      line-height: var(--ds-font-line-height-normal);
    }

    &__button {
      width: var(--ds-spacing-inset-lg);
      height: var(--ds-spacing-inset-lg);
    }

    &__subtitle {
      font: var(--ds-font-family-base);
    }

    &__search {
      display: flex;
      justify-content: space-between;
      gap: var(--ds-spacing-inline-xs);
      flex-wrap: wrap;
      width: 1196px;
      margin-top: var(--ds-spacing-inline-xs);
    }

    &__search-buttons {
      height: var(--ds-spacing-inset-lg);
      width: 590px;
      display: flex;
      justify-content: end;
      gap: var(--ds-spacing-inline-xs);
    }

    &__separator {
      width: 100%;
      margin: var(--ds-spacing-stack-md) 0;
      border-bottom: 1px solid var(--ds-color-silver);
    }

    &__data-table {
      width: 100%;
    }

    &__clear-button {
      font-style: normal;
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-baseline);
      line-height: var(--ds-font-line-height-normal);
      color: var(--ds-color-icon-interactive);
      text-decoration: underline;
      transition: 0.2s;
      margin-right: 3px;

      &:hover {
        opacity: 0.8;
      }
    }

    &__info {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }

    &__visualization {
      width: 100%;
      height: 100%;
    }

    &__info-title {
      font-style: normal;
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-baseline);
      line-height: var(--ds-font-line-height-normal);
      color: var(--ds-color-text-1);
    }

    &__info-description {
      font-style: normal;
      font-weight: var(--ds-font-weight-regular);
      font-size: var(--ds-font-size-sm);
      line-height: var(--ds-font-line-height-loose);
      color: var(--ds-color-text-2);
    }
  }

  ::v-deep {
    .ds-button--ghost .ds-icon {
      background-color: var(--ds-color-icon-2);
      cursor: pointer;
    }

    .ds-button--primary:disabled {
      cursor: default;
    }
  }
</style>
